<template>
  <busy-area :busy="busy">
    <FullScreenMap v-slot="{ fullScreen }">
      <Box
        :aspect="0.4"
        class="map-container"
        :class="{ '!h-screen !w-screen': fullScreen }"
      >
        <GMapMap
          ref="map"
          :center="center"
          :zoom="initialZoom ?? 13"
          :options="defaultOptions"
          :bounds_changed="true"
          class="h-fit"
          map-type-id="roadmap"
          @bounds_changed="boundsChanged"
        >
          <GMapMarker
            v-if="showLastKnownPostition && lastKnownPosition"
            :position="lastKnownPosition"
            :icon="{
              url: MapLocationIcon,
              scaledSize: { width: 100, height: 100 },
              anchor: { x: 50, y: 50 },
            }"
            :clickable="true"
          />
          <GMapCircle
            v-for="([point], i) in showStartpoints ? tours : []"
            :key="`${point.lat},${point.lng},${i}`"
            :radius="30"
            :center="point"
            :options="{
              clickable: true,
              strokeWeight: 0,
              fillColor: '#3DB699',
              fillOpacity: 0.5,
            }"
            @click="() => showTourDetails(point.tid)"
          />
          <GMapCircle
            v-for="point, i in showEndpoints ? tours.map((tour) => tour.at(-1)).filter((p) => p) as google.maps.LatLngLiteral[] : []"
            :key="`${point.lat},${point.lng},${i}`"
            :radius="30"
            :center="point"
            :options="{
              clickable: true,
              strokeWeight: 0,
              fillColor: '#7B61FF',
              fillOpacity: 0.5,
            }"
            @click="() => showTourDetails(point.tid)"
          />
        </GMapMap>
      </Box>
    </FullScreenMap>
    <ShowTourDetailsMapModal v-model:open="showTourModal" :tour-id="tourId" />
  </busy-area>
</template>

<script lang="ts" setup>
import BusyArea from '@components/utils/busy/BusyArea.vue';
import Box from '@components/utils/Box.vue';
import {
  computed, nextTick, onMounted, ref, watch,
} from 'vue';
import MapLocationIcon from '@images/mapLocation.svg?url';
import FullScreenMap from '@utils/FullScreenMap.vue';
import type { Position, Tour } from '@/entities/Entities';
import { defaultCenter, defaultOptions } from '@/utils/map';
import ShowTourDetailsMapModal from '@utils/maps/ShowTourDetailsMapModal.vue';

interface Props {
  busy?: boolean;
  showLastKnownPostition?: boolean;
  showEndpoints?: boolean;
  showStartpoints?: boolean;
  showTour?: boolean;
  autoCenter?: boolean;
  center?: google.maps.LatLngLiteral;
  positions: Position[];
  initialZoom?: number;
  displayedLinesThreshold: number;
}

const emit = defineEmits(['bounds']);
const props = withDefaults(defineProps<Props>(), {
  showTour: true,
  autoCenter: true,
  center: () => defaultCenter,
});
const map = ref();
const centerRef = ref<google.maps.LatLngLiteral>(defaultCenter);

// Ensure center always has valid finite coordinates
const center = computed(() => {
  const c = centerRef.value;
  if (c && typeof c.lat === 'number' && typeof c.lng === 'number' && isFinite(c.lat) && isFinite(c.lng)) {
    return c;
  }
  return defaultCenter;
});

const showTourModal = ref<boolean>(false);
const tourId = ref<string|null>(null);

const boundsChanged = async () => {
  await map.value.$mapPromise;
  const bounds = map.value.$mapObject.getBounds();
  if (bounds) {
    emit('bounds', bounds.toJSON());
  }
};

const preparedTours = computed(() => {
  if (!props.positions?.length) return [];
  const positions = props.positions.slice().sort((a, b) => a.t.localeCompare(b.t));
  const tours: Record<string, google.maps.LatLngLiteral[]> = {};
  for (const pos of positions) {
    // Skip positions with invalid coordinates
    if (typeof pos.lat !== 'number' || typeof pos.lng !== 'number' || !isFinite(pos.lat) || !isFinite(pos.lng)) {
      continue;
    }
    if (tours[pos.tid]) {
      tours[pos.tid].push(pos);
    } else {
      tours[pos.tid] = [pos];
    }
  }
  return Object.values(tours);
});

const tours = computed(() => preparedTours.value?.filter((tour) => tour.length >= 2) ?? []);
let drawnLines: google.maps.Polyline[] = [];

const clearMap = () => {
  const lines = drawnLines;
  if (lines) {
    for (const line of lines) {
      line.setMap(null);
    }
  }
  drawnLines = [];
};

const moveToCenter = (Lat: number, Lng: number) => {
  centerRef.value = { lat: Lat, lng: Lng };
};

defineExpose({
  clearMap,
  moveToCenter,
});

function deg2rad(deg: number) {
  return deg * (Math.PI / 180);
}

const getDistanceFromLatLonInKm = (lat1: number, lon1: number, lat2: number, lon2: number) => {
  const R = 6371; // Radius of the earth in km
  const dLat = deg2rad(lat2 - lat1); // deg2rad below
  const dLon = deg2rad(lon2 - lon1);
  const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(deg2rad(lat1)) * Math.cos(deg2rad(lat2)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const d = R * c; // Distance in km
  return d;
};

const showTourDetails = (id: string) => {
  showTourModal.value = false;
  tourId.value = null;
  nextTick(() => {
    tourId.value = id;
    showTourModal.value = true;
  });
};

watch(
  [tours, map, () => props.showTour, () => props.displayedLinesThreshold],
  async ([newTours, newMap, showTour, displayedLinesThreshold]) => {
    if (!newMap) return;
    await map.value.$mapPromise;
    clearMap();
    const newLines: google.maps.Polyline[] = [];
    if (!showTour) return;
    for (const tour of newTours) {
      const line = new google.maps.Polyline({
        strokeColor: '#CF464E',
        strokeOpacity: 0.5,
        editable: false,
      });
      if (
        !tour.some(({ lat, lng }, i) =>
          (i >= tour.length - 1
            ? false
            : getDistanceFromLatLonInKm(lat, lng, tour[i + 1].lat, tour[i + 1].lng) > displayedLinesThreshold))
      ) {
        line.setPath(tour);
        line.setMap(newMap.$mapObject);
        line.addListener('click', () => {
          const item = tour.at(0);
          if (item && item.tid) {
            showTourDetails(item.tid);
          }
        });
        newLines.push(line);
      }
    }
    drawnLines = newLines;
  },
  { immediate: true },
);

const computeCenter = () => {
  if (!props.autoCenter) return;
  if (!tours.value?.length) return;
  const positions = tours.value.flat();
  if (!positions.length) return;
  const bounds = new google.maps.LatLngBounds();
  positions.forEach((pos) => bounds.extend(pos));
  const centerLatLng = bounds.getCenter();
  if (centerLatLng) {
    centerRef.value = { lat: centerLatLng.lat(), lng: centerLatLng.lng() };
    map.value.fitBounds(bounds);
  }
};

const lastKnownPosition = computed(() => preparedTours.value.flat().at(-1));

watch(tours, () => computeCenter());

onMounted(async () => {
  await map.value.$mapPromise;
  computeCenter();
});

/* Currently unused - mapping of routes through directions API onto streets */
/*
watch(() => props.tours, async (newTours) => {
  const gMap: google.maps.Map = await map.value?.$mapPromise;

  if (newPositions.length < 2) return;

  //Initialize the Direction Service
  const service = new google.maps.DirectionsService();
  const directionsRenderer = new google.maps.DirectionsRenderer();

  directionsRenderer.setMap(gMap);

  const bounds = new google.maps.LatLngBounds();

  for (const positions of newTours) {
    const latLngs = positions.map((pos) => new google.maps.LatLng(parse(pos.gisLocation)));

    latLngs.forEach((latLng) => bounds.extend(latLng))

    const origin = latLngs.at(0);
    const destination = latLngs.at(-1);

    if (!origin || !destination) {
      throw new Error("Missing position data");
    }

    const route = await service.route({
      origin,
      destination,
      waypoints: latLngs.slice(1, -1).map((latLng) => ({ location: latLng, stopover: false })),
      optimizeWaypoints: false,
      travelMode: google.maps.TravelMode.BICYCLING,
    });

    directionsRenderer.setDirections(route);
  }

  gMap.setCenter(bounds.getCenter())
}, { immediate: false });
*/
</script>

<style lang="scss" scoped>
</style>
